import {
  ColumnDataTypes,
  FilterOperators,
  FilterOptionTypes,
  type ColumnConfig,
} from '@/components/DataTable';
import { AlertMuteButton } from '@/components/AlertMuteButton';
import { AlertUnmuteButton } from '@/components/AlertUnmuteButton';
import { AlertExcludeButton } from '@/components/AlertExcludeButton';
import { AlertRemoveExclusionButton } from '@/components/AlertRemoveExclusionButton';
import { AlertEditPolicyButton } from '@/components/AlertEditPolicyButton';
import { AlertPopOutButton } from '@/components/grouped-alerts/AlertPopOutButton';
import {
  ALERT_ORGANIZATION_SCOPE_FILTER_VALUE,
  ALERT_ORGANIZATION_SCOPE_LABEL,
  ORGANIZATION_ALERTS_FILTERABLE_FIELDS,
  OrganizationScanAlertsSortField,
  outcomeOptions,
  alertFeedbackStatusOptions,
  type GroupedAlert,
} from '@kirin/api-client/contracts/alerts';
import { AlertStatusCell, AlertCommentCell } from './FeedbackCells';
import { AlertSecurityGroupsCell } from '@/components/grouped-alerts/AlertSecurityGroupsCell';
import { listSecurityGroups } from '@kirin/api-client/api/security-groups';
import { listProjectsDataTable } from '@kirin/api-client/api/projects';
import { apiClient } from '@/lib/api-client';
import { ALERTS_FILTER_URL_KEYS } from './utils';
import { severityOptionsDescending } from '@kirin/api-client/contracts/shared/severity';
import { getSeverityLabel } from '@kirin/api-client/helpers/severity';
import {
  formatTypeFilterLabel,
  formatTitleFilterLabel,
} from '@kirin/api-client/helpers/alerts';
import { AlertSeverityOverrideCell } from '@/components/AlertSeverityOverrideCell';
import {
  TypeCell,
  FilePathCell,
  IssuesCell,
  ScoreCell,
  LastSeenCell,
  EndpointLastSeenCell,
  FirstSeenCell,
  ClientTypesCell,
  OutcomeCell,
  TITLE_COLUMN_WIDTH,
} from './CellRenderers';
import { UsersCell } from '@/components/grouped-alerts/UsersCell';
import { AlertEndpointsCell } from '@/components/grouped-alerts/AlertEndpointsCell';
import {
  FilePathFilterOption,
  SeverityFilterOption,
  UserFilterOption,
  ClientTypeFilterOption,
} from './FilterComponents';
import { getPlatformLabel } from '@/components/shared/platform/platform-utils';
import {
  fetchAlertFilterOptions,
  fetchAlertFileOptions,
  fetchMemberFilterOptions,
  getFilterOptionsQueryKey,
  getUserFilterOptionsQueryKey,
} from './api';
import { endpointFilterConfig } from '@/components/shared/shadow-ai';
import { TIMESTAMP_COLUMN_WIDTH_ABSOLUTE_ZONED } from '@/components/shared/timestampColumnWidth';

const ValuelessOperators = [
  { value: FilterOperators.IS_EMPTY, label: 'Is Empty' },
  { value: FilterOperators.IS_NOT_EMPTY, label: 'Is Not Empty' },
];

const DateOperators = [
  { value: FilterOperators.GREATER_THAN, label: 'Is After' },
  { value: FilterOperators.LESS_THAN, label: 'Is Before' },
  { value: FilterOperators.BETWEEN, label: 'Is Between' },
  ...ValuelessOperators,
];
/**
 * Generate column definitions for the organization alerts table.
 * Requires organizationId for dynamic filter options.
 *
 * `canFilterByUser` (owner/manager only) gates the Users column's filter:
 * its dropdown options are sourced from the org members list, an endpoint
 * RBAC-gated to owner/manager. For members the column stays visible and
 * sortable but is non-filterable, so no members fetch (→ 403) ever fires
 * (KIR-3266). Defaults to `false` — fail safe for any caller that omits it.
 */
export const createAlertsColumns = (
  organizationId: string,
  revampEnabled: boolean = false,
  canFilterByUser: boolean = false,
  includeEndpointsColumn: boolean = false,
  includeSecurityGroupColumn: boolean = false,
  // KIR-3800 / KIR-5086: owner/manager/support (the Security Groups page's
  // readers); the column itself stays ungated. Defaults to false — fail safe.
  canFilterBySecurityGroup: boolean = false,
  // KIR-5075: Project column + filter, gated by `projects_inventory` AND
  // `ai_risk_map` (resolved by the caller).
  includeProjectColumn: boolean = false
): ColumnConfig<GroupedAlert>[] => {
  // Users column: filterable (member-backed dropdown) for owner/manager,
  // otherwise a sortable-but-non-filterable column with no member fetch.
  const usersColumn: ColumnConfig<GroupedAlert> = canFilterByUser
    ? {
        id: 'users',
        header: 'Users',
        accessor: 'users',
        renderCell: ({ row }) => <UsersCell alert={row} />,
        width: 120,
        sortable: true,
        sortField: 'userCount',
        sortKey: 'uc',
        filterable: true,
        dataType: ColumnDataTypes.ENUM,
        filterOperators: [
          { value: FilterOperators.INCLUDES, label: 'Includes' },
          { value: FilterOperators.EXCLUDES, label: 'Excludes' },
          ...ValuelessOperators,
        ],
        statePersistence: {
          urlKey:
            ALERTS_FILTER_URL_KEYS[ORGANIZATION_ALERTS_FILTERABLE_FIELDS.USERS],
        },
        filterOptions: {
          type: FilterOptionTypes.DYNAMIC,
          getQueryKey: params =>
            getUserFilterOptionsQueryKey(organizationId, params),
          queryFn: params => fetchMemberFilterOptions(organizationId, params),
          queryConfig: {
            staleTime: 2 * 60 * 1000,
          },
          render: (option, isSelected) => (
            <UserFilterOption
              userId={option.value}
              userName={option.metadata?.name as string | null}
              userEmail={option.metadata?.email as string | null}
              isSelected={isSelected}
            />
          ),
        },
      }
    : {
        id: 'users',
        header: 'Users',
        accessor: 'users',
        renderCell: ({ row }) => <UsersCell alert={row} />,
        width: 120,
        sortable: true,
        sortField: 'userCount',
        sortKey: 'uc',
        filterable: false,
      };

  /*
    KIR-3800: Security Group column, gated by `security_groups_policies`.

    Fully server-side, like every other column here: the group set arrives on
    the alert (`securityGroups`), `sortField` names a real backend ORDER BY
    (MIN(security_groups.name), ungrouped collating under "Organization"), and the filter is a real
    `ORGANIZATION_ALERTS_FILTERABLE_FIELDS` entry matching any occurrence's
    group. Nothing about this column is derived from the current page.

    The filter's options come from the org's security-groups endpoint rather
    than a dedicated alerts filter-options endpoint: the group list is small and
    admin-curated, so a second source of truth would earn nothing.

    It requests `includeDeleted`. Alerts keep naming the group that triggered
    them after deletion, so options sourced from live groups only would leave a
    group visible in the rows but unselectable in the filter.
  */
  const securityGroupColumnBase = {
    id: 'securityGroups',
    header: 'Security Group',
    accessor: 'securityGroups' as const,
    renderCell: ({ row }: { row: GroupedAlert }) => (
      <AlertSecurityGroupsCell alert={row} />
    ),
    width: 170,
    sortable: true,
    sortField: OrganizationScanAlertsSortField.SecurityGroup,
    sortKey: 'sg',
  };

  /*
    The column stays visible and sortable for everyone: the group arrives on the
    alert itself and the sort is server-side on the alerts endpoint, neither of
    which needs elevated access.

    Only the FILTER is gated, and to owner/manager — a narrower set than the
    Users filter above. Two separate reasons, both load-bearing:

      1. Its options come from GET /security-groups. A plain member (the Alerts
         tab is anyRole) would fire that fetch, take a 403, and be left with a
         permanently empty dropdown — the KIR-3266 failure the Users column
         already guards against.
      2. The dropdown lists every group in the org, deleted ones included. That
         is the roster the Security Groups page owns, and that page is
         deliberately manager/owner. Support may read a group NAME off an alert
         it is triaging, but enumerating the roster is management. The endpoint
         would happily serve support (it is manager/owner/support); we choose
         not to ask.
  */
  const securityGroupColumn: ColumnConfig<GroupedAlert> =
    canFilterBySecurityGroup
      ? {
          ...securityGroupColumnBase,
          filterable: true,
          dataType: ColumnDataTypes.ENUM,
          // Deliberately NO valueless operators. "Is Empty"/"Is Not Empty" compile
          // to `expr IS NULL` / `IS NOT NULL`, and this column's HAVING expression
          // is COALESCE(group_id, '<organization sentinel>') — never null. They
          // would be silently dead controls. The "Organization" option is the
          // correct way to express "ungrouped", and it filters properly.
          filterOperators: [
            { value: FilterOperators.INCLUDES, label: 'Includes' },
            { value: FilterOperators.EXCLUDES, label: 'Excludes' },
          ],
          statePersistence: {
            urlKey:
              ALERTS_FILTER_URL_KEYS[
                ORGANIZATION_ALERTS_FILTERABLE_FIELDS.SECURITY_GROUPS
              ],
          },
          filterOptions: {
            type: FilterOptionTypes.DYNAMIC,
            // Distinct from `queryKeys.securityGroups.list`: that cache holds LIVE
            // groups only, and reusing its key here would serve the alerts filter
            // a list with the deleted groups missing (and vice versa).
            getQueryKey: () => [
              'securityGroups',
              organizationId,
              'includeDeleted',
            ],
            queryFn: async () => {
              // `includeDeleted`: alerts keep naming the group that triggered them
              // after it is deleted, so a filter sourced from live groups only
              // could not match rows that are visibly on screen. Deleted groups
              // are labelled so picking one is an informed choice.
              const groups = await listSecurityGroups(
                apiClient,
                organizationId,
                {
                  includeDeleted: true,
                }
              );
              return {
                options: [
                  // "Organization" is a real, selectable value: server-side the
                  // filter COALESCEs an ungrouped finding's NULL group to this
                  // sentinel, so it includes/excludes like any group. Listed first
                  // because it is the default scope for every ungrouped user.
                  {
                    value: ALERT_ORGANIZATION_SCOPE_FILTER_VALUE,
                    label: ALERT_ORGANIZATION_SCOPE_LABEL,
                  },
                  ...groups.map(group => ({
                    value: group.id,
                    label: group.deletedAt
                      ? `${group.name} (deleted)`
                      : group.name,
                  })),
                ],
                hasMore: false,
              };
            },
            queryConfig: {
              staleTime: 2 * 60 * 1000,
            },
            /*
            The dropdown's type-ahead narrows the fetched options locally instead
            of forwarding a `search` param.

            Required here, not just an optimisation: this source returns the
            org's whole (small, admin-curated) group list in one unpaginated call
            and has no server-side search to forward to. Left server-side, the
            typed term would go to a query that ignores it — and be stripped from
            the query key — so the list would never narrow.

            It also matches on the LABEL, which is what the user sees and types:
            "Organization", and a deleted group's "Retired (deleted)". The raw
            option value is a uuid and would match nothing.
          */
            clientSideSearch: true,
          },
        }
      : { ...securityGroupColumnBase, filterable: false };

  /*
    KIR-5075: Project column, gated by `projects_inventory` AND `ai_risk_map`
    (both resolved by the caller, DataTableAlertsTab/index.tsx).

    The FILTER is the point: `projects[...]` matches an alert when ANY of its
    occurrences was scanned in a selected project (scan_requests.project_id),
    which is what Risk Map deep links (`org-alt.pr[includes]=<projectId>`)
    rely on. The cell shows the alert's representative (latest occurrence)
    `project`, which the backend attaches to every alert type while
    projects_inventory AND ai_risk_map are on (alerts-helpers.ts
    `projectAllTypesEnabled`); alerts whose scan has no linked project
    render "—".

    So cell and filter can disagree: the cell is the LATEST occurrence's
    project (possibly none), the filter matches ANY occurrence. A row kept
    by a project filter may show "—" or a different project. This is
    deliberate: the backend's grouped project select
    (findings-grouped-query-planner.ts `buildGroupedProjectSelectFields`)
    also serves the pre-existing secret attribution, so it is not changed
    to "latest non-null" here.

    Options come from the projects inventory list (manager/owner/support only,
    same set as `canFilterByUser`), so members get a non-filterable column and
    never fire that request. The list hides projects with no owned files, so a
    deep-linked id outside it shows its raw id in the tooltip.
  */
  const projectColumnBase = {
    id: ORGANIZATION_ALERTS_FILTERABLE_FIELDS.PROJECTS,
    header: 'Project',
    accessor: 'project' as const,
    renderCell: ({ row }: { row: GroupedAlert }) => (
      <span className="truncate">{row.project?.name ?? '—'}</span>
    ),
    width: 160,
    sortable: false,
  };
  const projectColumn: ColumnConfig<GroupedAlert> = canFilterByUser
    ? {
        ...projectColumnBase,
        filterable: true,
        dataType: ColumnDataTypes.ENUM,
        filterOperators: [
          { value: FilterOperators.INCLUDES, label: 'Includes' },
          { value: FilterOperators.EXCLUDES, label: 'Excludes' },
          ...ValuelessOperators,
        ],
        statePersistence: {
          urlKey:
            ALERTS_FILTER_URL_KEYS[
              ORGANIZATION_ALERTS_FILTERABLE_FIELDS.PROJECTS
            ],
        },
        filterOptions: {
          type: FilterOptionTypes.DYNAMIC,
          getQueryKey: params => [
            'organizations',
            organizationId,
            'alerts',
            'project-filter-options',
            ...(params.search ? [params.search] : []),
          ],
          queryFn: async params => {
            const page = await listProjectsDataTable(
              apiClient,
              organizationId,
              {
                limit: 100,
                ...(params.search ? { search: params.search } : {}),
              }
            );
            return {
              options: page.data.map(project => ({
                value: project.id,
                label: project.name,
              })),
              hasMore: false,
            };
          },
          queryConfig: {
            staleTime: 2 * 60 * 1000,
          },
        },
      }
    : { ...projectColumnBase, filterable: false };

  // KIR-3035: Endpoints column — single endpoint shows its name, multiple show
  // the count; click opens the "Affected Endpoints" popover + details dialog.
  const endpointsColumn: ColumnConfig<GroupedAlert> = {
    id: ORGANIZATION_ALERTS_FILTERABLE_FIELDS.ENDPOINTS,
    header: 'Endpoints',
    accessor: 'endpoints',
    renderCell: ({ row }) => <AlertEndpointsCell alert={row} />,
    width: 160,
    // KIR-3035: sort by the number of distinct endpoints an alert was seen on.
    sortable: true,
    sortField: 'endpointCount',
    sortKey: 'ep',
    // KIR-5098: the shared endpoint name-or-ID filter (Contains / Equals +
    // Matches). Name = the snapshot taken when the alert fired.
    ...endpointFilterConfig({
      getQueryKey: params =>
        getFilterOptionsQueryKey(
          organizationId,
          ORGANIZATION_ALERTS_FILTERABLE_FIELDS.ENDPOINTS,
          params
        ),
      queryFn: async params => ({
        options: await fetchAlertFilterOptions(
          organizationId,
          ORGANIZATION_ALERTS_FILTERABLE_FIELDS.ENDPOINTS,
          params
        ),
      }),
    }),
    statePersistence: {
      urlKey:
        ALERTS_FILTER_URL_KEYS[ORGANIZATION_ALERTS_FILTERABLE_FIELDS.ENDPOINTS],
    },
  };

  // KIR-5537: Last Seen on the filtered endpoint (and person) only. The
  // endpoint page's links set it so the list matches the Endpoints tab's
  // 7-day count; the column shows only while that filter is on.
  const endpointLastSeenColumn: ColumnConfig<GroupedAlert> = {
    id: ORGANIZATION_ALERTS_FILTERABLE_FIELDS.ENDPOINT_LAST_SEEN_AT,
    header: 'Last Seen on Endpoint',
    accessor: 'endpointLastSeenAt',
    renderCell: ({ row }) => <EndpointLastSeenCell row={row} />,
    timestamp: true,
    // KIR-5639: the one-line header outgrows the relative and UTC widths.
    minWidth: TIMESTAMP_COLUMN_WIDTH_ABSOLUTE_ZONED,
    hidden: true,
    showWhenFiltered: true,
    filterable: true,
    dataType: ColumnDataTypes.DATE,
    filterOperators: DateOperators.filter(
      op =>
        op.value !== FilterOperators.IS_EMPTY &&
        op.value !== FilterOperators.IS_NOT_EMPTY
    ),
    statePersistence: {
      urlKey:
        ALERTS_FILTER_URL_KEYS[
          ORGANIZATION_ALERTS_FILTERABLE_FIELDS.ENDPOINT_LAST_SEEN_AT
        ],
    },
    filterOptions: {
      type: FilterOptionTypes.STATIC,
      options: [],
    },
  };

  const clientTypesColumn: ColumnConfig<GroupedAlert> = {
    id: 'clientTypes',
    header: 'Agent Platform',
    accessor: 'clientTypes',
    renderCell: ({ row }) => <ClientTypesCell row={row} />,
    width: 200,
    sortable: true,
    sortField: 'clientTypes',
    sortKey: 'ct',
    filterable: true,
    dataType: ColumnDataTypes.ENUM,
    filterOperators: [
      { value: FilterOperators.INCLUDES, label: 'Includes' },
      { value: FilterOperators.EXCLUDES, label: 'Excludes' },
      ...ValuelessOperators,
    ],
    statePersistence: {
      urlKey:
        ALERTS_FILTER_URL_KEYS[
          ORGANIZATION_ALERTS_FILTERABLE_FIELDS.CLIENT_TYPES
        ],
    },
    filterOptions: {
      type: FilterOptionTypes.DYNAMIC,
      getQueryKey: params =>
        getFilterOptionsQueryKey(organizationId, 'clientTypes', params),
      queryFn: params =>
        fetchAlertFilterOptions(organizationId, 'clientTypes', params),
      queryConfig: {
        staleTime: 2 * 60 * 1000,
      },
      labelFormatter: getPlatformLabel,
      render: (option, isSelected) => (
        <ClientTypeFilterOption
          clientType={option.value}
          isSelected={isSelected}
        />
      ),
    },
  };

  // KIR-3769: the Title/Type column.
  // - revamp ON: a composite "Title" filter (field `title`, value
  //   `<subPolicyKey>::<type>`) so two titles sharing one `type` slug become
  //   separate, individually-filterable options, and the option label matches
  //   the rendered cell. Type-ahead is client-side (the composite key is not
  //   the label). Uses its own `ti` url key; the legacy `t` (type) deep links
  //   are kept working by the hidden back-compat `type` column below.
  // - revamp OFF: byte-identical to the pre-KIR-3769 Type column (field
  //   `type`, server-side search, `t` url key).
  const titleOrTypeColumn: ColumnConfig<GroupedAlert> = revampEnabled
    ? {
        id: ORGANIZATION_ALERTS_FILTERABLE_FIELDS.TITLE,
        header: 'Title',
        accessor: 'type',
        renderCell: ({ row }) => (
          <TypeCell row={row} revampEnabled={revampEnabled} />
        ),
        width: TITLE_COLUMN_WIDTH,
        sortable: true,
        // Sort still uses the `type` slug server-side (unchanged); only the
        // filter identity changed. Keep the `t` sort key so sort deep links
        // are unaffected.
        sortField: 'type',
        sortKey: 't',
        filterable: true,
        dataType: ColumnDataTypes.ENUM,
        filterOperators: [
          { value: FilterOperators.INCLUDES, label: 'Includes' },
          { value: FilterOperators.EXCLUDES, label: 'Excludes' },
          ...ValuelessOperators,
        ],
        statePersistence: {
          urlKey:
            ALERTS_FILTER_URL_KEYS[ORGANIZATION_ALERTS_FILTERABLE_FIELDS.TITLE],
        },
        filterOptions: {
          type: FilterOptionTypes.DYNAMIC,
          getQueryKey: params =>
            getFilterOptionsQueryKey(
              organizationId,
              ORGANIZATION_ALERTS_FILTERABLE_FIELDS.TITLE,
              params
            ),
          queryFn: params =>
            fetchAlertFilterOptions(
              organizationId,
              ORGANIZATION_ALERTS_FILTERABLE_FIELDS.TITLE,
              params
            ),
          queryConfig: {
            staleTime: 2 * 60 * 1000,
          },
          // Composite key values aren't human-readable; search the labels
          // locally so typing a displayed title works.
          clientSideSearch: true,
          labelFormatter: formatTitleFilterLabel,
          // The active-filter tooltip resolves its text from the FETCHED
          // options and only falls back to this formatter on a miss. A
          // deep-linked value can legitimately be missing (options not yet
          // resolved, confidence cutoff, the 200-option limit, or label
          // dedupe), and without this the tooltip shows the raw composite.
          tooltip: { labelFormatter: formatTitleFilterLabel },
        },
      }
    : {
        id: 'type',
        header: 'Type',
        accessor: 'type',
        renderCell: ({ row }) => (
          <TypeCell row={row} revampEnabled={revampEnabled} />
        ),
        // KIR-2941: shared with the personal alerts table via the constant.
        width: TITLE_COLUMN_WIDTH,
        sortable: true,
        sortField: 'type',
        sortKey: 't',
        filterable: true,
        dataType: ColumnDataTypes.STRING,
        filterOperators: [
          { value: FilterOperators.INCLUDES, label: 'Includes' },
          { value: FilterOperators.EXCLUDES, label: 'Excludes' },
          ...ValuelessOperators,
        ],
        statePersistence: {
          urlKey:
            ALERTS_FILTER_URL_KEYS[ORGANIZATION_ALERTS_FILTERABLE_FIELDS.TYPE],
        },
        filterOptions: {
          type: FilterOptionTypes.DYNAMIC,
          getQueryKey: params =>
            getFilterOptionsQueryKey(organizationId, 'type', params),
          queryFn: params =>
            fetchAlertFilterOptions(organizationId, 'type', params),
          queryConfig: {
            staleTime: 2 * 60 * 1000, // 5 minutes
          },
          // KIR-2381: drives dropdown labels off the same registry as the
          // cell renderer so option labels match what users see in the table.
          labelFormatter: (type: string) =>
            formatTypeFilterLabel(type, revampEnabled),
          // Same reason as the Title column: the tooltip falls back to this
          // when a deep-linked value is absent from the fetched options.
          tooltip: {
            labelFormatter: (type: string) =>
              formatTypeFilterLabel(type, revampEnabled),
          },
        },
      };

  // KIR-3769 back-compat: revamp-on the visible column filters on `title`
  // (url key `ti`), so a legacy "View Details" deep link that still targets the
  // `type` filter (url key `t`, emitted by the Go agent + VS Code extension —
  // KIR-2659) would otherwise be silently dropped by the DataTable's
  // url-key→field map. This hidden column keeps the `type` filter field alive
  // (same `t` url key + server-side type options) WITHOUT rendering a second
  // visible header/cell, so those deep links resolve exactly as before. It is
  // only added when the composite title column has taken over the `t` slug.
  const hiddenTypeCompatColumn: ColumnConfig<GroupedAlert> = {
    id: ORGANIZATION_ALERTS_FILTERABLE_FIELDS.TYPE,
    header: 'Type',
    accessor: 'type',
    hidden: true,
    filterable: true,
    dataType: ColumnDataTypes.STRING,
    filterOperators: [
      { value: FilterOperators.INCLUDES, label: 'Includes' },
      { value: FilterOperators.EXCLUDES, label: 'Excludes' },
      ...ValuelessOperators,
    ],
    statePersistence: {
      urlKey:
        ALERTS_FILTER_URL_KEYS[ORGANIZATION_ALERTS_FILTERABLE_FIELDS.TYPE],
    },
    filterOptions: {
      type: FilterOptionTypes.DYNAMIC,
      getQueryKey: params =>
        getFilterOptionsQueryKey(organizationId, 'type', params),
      queryFn: params =>
        fetchAlertFilterOptions(organizationId, 'type', params),
      queryConfig: {
        staleTime: 2 * 60 * 1000,
      },
      labelFormatter: (type: string) =>
        formatTypeFilterLabel(type, revampEnabled),
    },
  };

  return [
    {
      id: 'lastSeenAt',
      header: 'Last Seen',
      accessor: 'lastSeenAt',
      renderCell: ({ row }) => <LastSeenCell row={row} />,
      // Width follows the Timestamp Format and display zone (KIR-1285).
      timestamp: true,
      sortable: true,
      sortField: 'lastSeenAt',
      sortKey: 'ls',
      filterable: true,
      dataType: ColumnDataTypes.DATE,
      filterOperators: DateOperators,
      statePersistence: {
        urlKey:
          ALERTS_FILTER_URL_KEYS[
            ORGANIZATION_ALERTS_FILTERABLE_FIELDS.LAST_SEEN_AT
          ],
      },
      filterOptions: {
        type: FilterOptionTypes.STATIC,
        options: [],
      },
    },
    usersColumn,
    ...(includeSecurityGroupColumn ? [securityGroupColumn] : []),
    ...(includeProjectColumn ? [projectColumn] : []),
    ...(includeEndpointsColumn
      ? [endpointsColumn, endpointLastSeenColumn]
      : []),
    titleOrTypeColumn,
    ...(revampEnabled ? [hiddenTypeCompatColumn] : []),
    {
      id: 'severity',
      header: 'Severity',
      // Severity override dropdown (+ its override dot) must stay visible.
      clipOverflow: false,
      accessor: 'severity',
      renderCell: ({ row }) => (
        <AlertSeverityOverrideCell
          scope={{ kind: 'organization', organizationId }}
          alert={row}
        />
      ),
      width: 140,
      sortable: true,
      sortField: 'severity',
      sortKey: 'sev',
      filterable: true,
      dataType: ColumnDataTypes.STRING,
      filterOperators: [
        { value: FilterOperators.INCLUDES, label: 'Includes' },
        { value: FilterOperators.EXCLUDES, label: 'Excludes' },
        ...ValuelessOperators,
      ],
      statePersistence: {
        urlKey:
          ALERTS_FILTER_URL_KEYS[
            ORGANIZATION_ALERTS_FILTERABLE_FIELDS.SEVERITY
          ],
      },
      filterOptions: {
        type: FilterOptionTypes.STATIC,
        options: severityOptionsDescending,
        render: (option, isSelected) => (
          <SeverityFilterOption
            severity={option.value}
            isSelected={isSelected}
          />
        ),
        tooltip: {
          labelFormatter: getSeverityLabel,
        },
      },
    },
    {
      id: 'outcome',
      header: 'Outcome',
      accessor: 'outcome',
      renderCell: ({ row }) => <OutcomeCell row={row} />,
      width: 130,
      sortable: true,
      sortField: 'outcome',
      sortKey:
        ALERTS_FILTER_URL_KEYS[ORGANIZATION_ALERTS_FILTERABLE_FIELDS.OUTCOME],
      filterable: true,
      dataType: ColumnDataTypes.ENUM,
      filterOperators: [
        { value: FilterOperators.INCLUDES, label: 'Includes' },
        { value: FilterOperators.EXCLUDES, label: 'Excludes' },
        ...ValuelessOperators,
      ],
      statePersistence: {
        urlKey:
          ALERTS_FILTER_URL_KEYS[ORGANIZATION_ALERTS_FILTERABLE_FIELDS.OUTCOME],
      },
      filterOptions: {
        type: FilterOptionTypes.STATIC,
        options: outcomeOptions.map(o => ({ value: o.value, label: o.label })),
      },
    },
    {
      id: 'filePath',
      header: 'File',
      accessor: 'filePath',
      renderCell: ({ row }) => <FilePathCell row={row} />,
      width: 320,
      // File column is display-only: the cell shows a resolved label
      // (real path from `location`, or a friendly sentinel description),
      // which server-side sort on the stored `filePath` cannot match. KIR-3154.
      sortable: false,
      filterable: true,
      dataType: ColumnDataTypes.STRING,
      filterOperators: [
        { value: FilterOperators.INCLUDES, label: 'Includes' },
        { value: FilterOperators.EXCLUDES, label: 'Excludes' },
        { value: FilterOperators.CONTAINS, label: 'Contains' },
        { value: FilterOperators.NOT_CONTAINS, label: 'Does not contain' },
        ...ValuelessOperators,
      ],
      statePersistence: {
        urlKey:
          ALERTS_FILTER_URL_KEYS[
            ORGANIZATION_ALERTS_FILTERABLE_FIELDS.FILE_PATH
          ],
      },
      filterOptions: {
        type: FilterOptionTypes.DYNAMIC,
        getQueryKey: params =>
          getFilterOptionsQueryKey(organizationId, 'filePath', {
            search: params.search,
            limit: params.limit,
          }),
        queryFn: params => fetchAlertFileOptions(organizationId, params),
        queryConfig: {
          staleTime: 2 * 60 * 1000,
        },
        dropdown: {
          width: 520,
        },
        pagination: {
          mode: 'offset',
          limit: 5,
        },
        render: (option, isSelected) => (
          <FilePathFilterOption
            filePath={option.value}
            isSelected={isSelected}
          />
        ),
      },
    },
    clientTypesColumn,
    {
      id: 'occurrenceCount',
      header: 'Instances',
      accessor: 'occurrenceCount',
      renderCell: ({ row }) => <IssuesCell row={row} />,
      width: 130,
      sortable: true,
      sortField: 'occurrenceCount',
      sortKey: 'oc',
      filterable: true,
      dataType: ColumnDataTypes.NUMBER,
      filterOperators: [
        { value: FilterOperators.INCLUDES, label: 'Includes' },
        { value: FilterOperators.EXCLUDES, label: 'Excludes' },
        ...ValuelessOperators,
      ],
      statePersistence: {
        urlKey:
          ALERTS_FILTER_URL_KEYS[
            ORGANIZATION_ALERTS_FILTERABLE_FIELDS.OCCURRENCE_COUNT
          ],
      },
      range: {
        min: 1,
        max: 100,
        step: 1,
      },
    },
    {
      id: 'score',
      header: 'Score',
      accessor: 'score',
      renderCell: ({ row }) => <ScoreCell row={row} />,
      width: 110,
      sortable: true,
      sortField: 'score',
      sortKey: 'sc',
      filterable: true,
      dataType: ColumnDataTypes.NUMBER,
      filterOperators: [
        { value: FilterOperators.INCLUDES, label: 'Includes' },
        { value: FilterOperators.EXCLUDES, label: 'Excludes' },
        ...ValuelessOperators,
      ],
      statePersistence: {
        urlKey:
          ALERTS_FILTER_URL_KEYS[ORGANIZATION_ALERTS_FILTERABLE_FIELDS.SCORE],
      },
      range: {
        min: 0,
        max: 100,
        step: 1,
      },
    },
    {
      id: 'firstSeenAt',
      header: 'First Seen',
      accessor: 'firstSeenAt',
      renderCell: ({ row }) => <FirstSeenCell row={row} />,
      // Width follows the Timestamp Format and display zone (KIR-1285).
      timestamp: true,
      sortable: true,
      sortField: 'firstSeenAt',
      sortKey: 'fs',
      filterable: true,
      dataType: ColumnDataTypes.DATE,
      filterOperators: DateOperators,
      statePersistence: {
        urlKey:
          ALERTS_FILTER_URL_KEYS[
            ORGANIZATION_ALERTS_FILTERABLE_FIELDS.FIRST_SEEN_AT
          ],
      },
      filterOptions: {
        type: FilterOptionTypes.STATIC,
        options: [],
      },
    },
  ];
};

/**
 * Factory for the Actions column that renders mute/unmute and
 * exclude/remove-exclusion controls per row.
 *
 * The column is consumed as an additive append to the columns array only
 * when the viewer is owner / manager, so member views keep the exact
 * pre-KIR-843 layout. Each individual button is itself role-gated (renders
 * `null` when the role check fails), so the cell composes naturally without
 * per-button orchestration here.
 */
/**
 * KIR-4440 Status column (feedback verdict). Sits left of the Actions column, gated by
 * the `alert_feedback` flag at the call site. Filterable enum (INCLUDES /
 * EXCLUDES). `canEdit` (owner/manager) toggles the editable dropdown vs a
 * read-only badge — the verdict is visible to everyone, editable by admins only.
 */
export function createAlertStatusColumn(
  organizationId: string,
  canEdit: boolean
): ColumnConfig<GroupedAlert> {
  return {
    id: 'status',
    header: 'Status',
    // The status dropdown must stay fully visible.
    clipOverflow: false,
    accessor: 'feedback',
    renderCell: ({ row }) => (
      <AlertStatusCell
        alert={row}
        organizationId={organizationId}
        canEdit={canEdit}
      />
    ),
    width: 160,
    sortable: false,
    filterable: true,
    dataType: ColumnDataTypes.ENUM,
    filterOperators: [
      { value: FilterOperators.INCLUDES, label: 'Includes' },
      { value: FilterOperators.EXCLUDES, label: 'Excludes' },
    ],
    statePersistence: {
      urlKey:
        ALERTS_FILTER_URL_KEYS[ORGANIZATION_ALERTS_FILTERABLE_FIELDS.STATUS],
    },
    filterOptions: {
      type: FilterOptionTypes.STATIC,
      options: alertFeedbackStatusOptions.map(o => ({
        value: o.value,
        label: o.label,
      })),
    },
  };
}

/**
 * KIR-4440 Comment column (free-text feedback). Sits left of the Actions column, gated by the
 * `alert_feedback` flag at the call site. Filterable text (CONTAINS — the
 * comment "search"). `canEdit` (any member except support) toggles the editable
 * popover vs read-only text.
 */
export function createAlertCommentColumn(
  organizationId: string,
  canEdit: boolean
): ColumnConfig<GroupedAlert> {
  return {
    id: 'comment',
    header: 'Comment',
    accessor: 'feedback',
    renderCell: ({ row }) => (
      <AlertCommentCell
        alert={row}
        organizationId={organizationId}
        canEdit={canEdit}
      />
    ),
    width: 280,
    sortable: false,
    filterable: true,
    dataType: ColumnDataTypes.TEXT,
    filterOperators: [
      { value: FilterOperators.CONTAINS, label: 'Contains' },
      { value: FilterOperators.NOT_CONTAINS, label: 'Does not contain' },
      ...ValuelessOperators,
    ],
    statePersistence: {
      urlKey:
        ALERTS_FILTER_URL_KEYS[ORGANIZATION_ALERTS_FILTERABLE_FIELDS.COMMENT],
    },
  };
}

export function createOrganizationAlertsActionsColumn(
  organizationId: string
): ColumnConfig<GroupedAlert> {
  return {
    id: 'actions',
    header: 'Actions',
    // Action buttons must stay fully visible.
    clipOverflow: false,
    // KIR-4700: condensed to icon-only buttons. 32 (Mute/Unmute icon-sm) +
    // 32 (Exclude/Remove icon-sm) + 32 (Edit Policy icon-sm) + 32 (pop-out
    // icon-sm) + 24 (gap-2 x3) + 24 (TableCell px-3) + 8 (pr-2 trailing) =
    // 184. Keep in lock-step with the `iconOnly` sizing in AlertMuteButton /
    // AlertExcludeButton / AlertEditPolicyButton / AlertPopOutButton and the
    // cell wrapper gap/padding below.
    width: 184,
    sortable: false,
    filterable: false,
    renderCell: ({ row }) => {
      const scope = {
        kind: 'organization' as const,
        organizationId,
      };
      const analyticsContext = { severity: row.severity, type: row.type };
      // Suppress click-through to row expansion. Buttons are interactive and
      // own their own focus semantics; clicking a button should not also
      // toggle the row.
      return (
        <div
          className="flex items-center justify-start gap-2 pr-2"
          onClick={event => event.stopPropagation()}
          onPointerDown={event => event.stopPropagation()}
        >
          {row.mute?.muted ? (
            <AlertUnmuteButton
              scope={scope}
              alertKey={row.alertKey}
              mute={row.mute}
              analyticsContext={analyticsContext}
              iconOnly
            />
          ) : (
            <AlertMuteButton
              scope={scope}
              alertKey={row.alertKey}
              analyticsContext={analyticsContext}
              iconOnly
            />
          )}
          {row.exclude?.excluded ? (
            <AlertRemoveExclusionButton
              scope={scope}
              alertKey={row.alertKey}
              exclude={row.exclude}
              analyticsContext={analyticsContext}
              iconOnly
            />
          ) : (
            <AlertExcludeButton
              scope={scope}
              alertKey={row.alertKey}
              analyticsContext={analyticsContext}
              findingType={row.type}
              iconOnly
            />
          )}
          <AlertEditPolicyButton
            scope={{ kind: 'organization', organizationId }}
            policyKey={row.policyKey}
            subPolicyKey={row.subPolicyKey}
            alertKey={row.alertKey}
            iconOnly
          />
          <AlertPopOutButton
            alertKey={row.alertKey}
            organizationId={organizationId}
          />
        </div>
      );
    },
  };
}
